import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import * as Sentry from '@sentry/react';
import { AlertDialog, Button, Callout, Flex, Link, Modal, Text } from '@homeexchange/design';
import { getConversationsV2, STAY_TYPE_WITH_GP } from '@homeexchange/conversations';
import { isSameDay } from '@homeexchange/shared';

import i18n from '../../../i18n';

import Api from '../../../api/Api';

import Availability, { EXCHANGE_TYPE_ID_TO_STAY_REQUEST_TYPE_NAME } from '../../../models/Availability';
import Conversation from '../../../models/Conversation';
import HomeModel from '../../../models/Home';
import UserModel from '../../../models/User';

import MessageField from './MessageField';
import withAnalytics from '../../analytics/withAnalytics';
import CalendarUtils from '../../../utils/CalendarUtils';
import Utils from '../../../utils/utils';
import Analytics from '../../../utils/analytics';
import { createStayRequestV3 } from '../../../actions/stayRequest';
import { fetchConversationsStats } from '../../../actions/conversations';
import { NAVIGATION_ACTION, NAVIGATION_AREAS, NAVIGATION_TEXT } from '../../analytics/analytics-constants';
import useGTM from '../../analytics/useGTM';
import CounterGuestNb from '../../search/filters/CounterGuestNb';
import ExchangeTypeSelect from '../../widget/ExchangeTypeSelect';
import DateRangePickerSelect from '../../widget/DateRangePickerSelect';

export class MainView extends React.PureComponent {
    static propTypes = {
        showModal: PropTypes.bool.isRequired,
        onCloseModal: PropTypes.func.isRequired,
        user: PropTypes.instanceOf(UserModel).isRequired,
        home: PropTypes.instanceOf(HomeModel).isRequired,
        availabilities: PropTypes.arrayOf(PropTypes.instanceOf(Availability)),
        onMessageSent: PropTypes.func.isRequired,
        track: PropTypes.func.isRequired,
        updateLastSearch: PropTypes.func.isRequired,
        accessToken: PropTypes.string.isRequired,
        conversationsStats: PropTypes.object,
        actions: PropTypes.shape({
            createStayRequestV3: PropTypes.func.isRequired
        }),
        fetchConversationsStats: PropTypes.func
    };

    static defaultProps = {
        availabilities: []
    };

    static DEFAULT_ADULTS_NUMBER = 1;

    static DEFAULT_GUESTS_DATA = {
        size: {
            beds: {
                adults: MainView.DEFAULT_ADULTS_NUMBER,
                children: 0,
                babies: 0
            }
        }
    };

    constructor(props) {
        super(props);

        this.state = {
            startOn: null,
            startOnFromLocalStorage: null,
            endOn: null,
            endOnFromLocalStorage: null,
            exchangeType: undefined,
            exchangeTypeFromLocalStorage: undefined,
            previousConversation: null,
            isCalendarOpen: false,
            disableSubmit: false,
            sending: false,
            openGuestNbPopup: false,
            guestsData: MainView.DEFAULT_GUESTS_DATA,
            guestsDataFromLocalStorage: null,
            lastSearch: null,
            showAlertDialog: false,
            alertDialogProps: {},
            hasMessageError: false,
            numberOfNightsFromCalendar: null,
            hasTypeExchangeError: false,
            datesError: null,
            message: ''
        };

        this.invalidFields = [];

        this.datesChange = this.datesChange.bind(this);
        this.nbGuestChange = this.nbGuestChange.bind(this);
        this.setPreviouSearchData = this.setPreviouSearchData.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onMessageSuccess = this.onMessageSuccess.bind(this);
        this.onMessageFailure = this.onMessageFailure.bind(this);
        this.exchangeTypeChange = this.exchangeTypeChange.bind(this);
        this.getExchangeTypeOnDateChange = this.getExchangeTypeOnDateChange.bind(this);
        this.openGuestNbPopup = this.openGuestNbPopup.bind(this);
        this.onChangeMessage = this.onChangeMessage.bind(this);
        this.handleOnSubmit = this.handleOnSubmit.bind(this);

        this.divRef = React.createRef();
        this.popupGuestNb = React.createRef();
        this.guestNb = React.createRef();
    }

    componentDidMount() {
        this.getPreviousConversationForHome(this.props.home);
        this.loadDateRangeFromLocalLastSearch();
        this.props.fetchConversationsStats();
    }

    componentDidUpdate() {
        this.handleFormValidation();
    }

    // find a previous conversation between the two users for the same home
    getPreviousConversationForHome(home) {
        // include archived/deleted statuses so the "already contacted" banner still
        // shows even if the conversation was archived or deleted.
        return getConversationsV2({
            filters: { 'e.home': home.id, status: Conversation.getContactedStatuses() },
            offset: 0,
            limit: 1
        }).then((response) => {
            if (response.conversations && response.conversations.length > 0) {
                this.setState({
                    previousConversation: _.first(response.conversations)
                });
            }
            return response;
        });
    }

    openGuestNbPopup(open) {
        const openGuestNbPopup = Boolean(open);
        if (openGuestNbPopup) {
            Utils.openModalsFullScreen();
        } else {
            Utils.closeModalsFullScreen();
        }
        this.setState({
            openGuestNbPopup
        });
    }

    // preset last date range based on user's last search
    loadDateRangeFromLocalLastSearch() {
        return Api.Search.getLocalLastSearch(this.props.user.id)
            .then((localResults) => {
                let start = null;
                let end = null;
                let guestsData = MainView.DEFAULT_GUESTS_DATA;
                let exchangeType = undefined;

                if (_.isObject(localResults) && _.has(localResults, 'query')) {
                    if (_.has(localResults.query, 'calendar')) {
                        if (localResults.query.calendar.date_ranges?.length === 1) {
                            start = _.result(localResults.query.calendar.date_ranges[0], 'from', null);
                            end = _.result(localResults.query.calendar.date_ranges[0], 'to', null);
                        } else if (!localResults.query.calendar.date_ranges) {
                            Object.assign(localResults.query.calendar, {
                                date_ranges: [{}]
                            });
                        }

                        // Start date is in the past, remove dates
                        if (start && moment().isAfter(moment(start))) {
                            start = null;
                            end = null;
                        }

                        let valueFromLocalResults = undefined;

                        // compute exchangeType.
                        if (_.has(localResults.query.availability, 'reciprocal')) {
                            valueFromLocalResults = localResults.query.availability.reciprocal
                                ? Availability.RECIPROCAL.value
                                : Availability.NON_RECIPROCAL.value;
                        }

                        exchangeType = this.getExchangeTypeOnDateChange(start, end, valueFromLocalResults);
                    }

                    if (
                        localResults.query?.home?.size?.beds &&
                        Object.keys(localResults.query?.home?.size?.beds).length
                    ) {
                        const { adults, children, babies } = localResults.query.home.size.beds;

                        guestsData = {
                            size: {
                                beds: {
                                    adults: adults ? adults : MainView.DEFAULT_ADULTS_NUMBER,
                                    children: children ? children : 0,
                                    babies: babies ? babies : 0
                                }
                            }
                        };
                    }
                }

                this.setState({
                    lastSearch: localResults
                });

                return { start, end, guestsData, exchangeType };
            })
            .then(this.setPreviouSearchData);
    }

    setPreviouSearchData(lastSearchData) {
        if (lastSearchData.start && lastSearchData.end) {
            const range = moment.range(lastSearchData.start, lastSearchData.end);
            let setDates = !CalendarUtils.overlapsBookedPeriod(range, this.props.availabilities);
            if (
                setDates &&
                this.props.home.get('contact_allowed') === HomeModel.CONTACT_NOT_ALLOWED_ON_UNAVAILABLE
            ) {
                // The user doesn't want to be contacted outside his available periods, check dates
                setDates = CalendarUtils.inAvailablePeriod(range, this.props.availabilities);
            }
            if (setDates && !this.state.startOn && !this.state.endOn) {
                // Check that the user hasn't updated the form already
                this.setState({
                    startOn: lastSearchData.start,
                    startOnFromLocalStorage: lastSearchData.start,
                    endOn: lastSearchData.end,
                    endOnFromLocalStorage: lastSearchData.end,
                    guestsData: lastSearchData.guestsData,
                    guestsDataFromLocalStorage: lastSearchData.guestsData,
                    exchangeType: lastSearchData.exchangeType,
                    exchangeTypeFromLocalStorage: lastSearchData.exchangeType
                });
            } else if (!setDates) {
                this.setState({
                    guestsData: lastSearchData.guestsData,
                    guestsDataFromLocalStorage: lastSearchData.guestsData,
                    exchangeType: lastSearchData.exchangeType,
                    exchangeTypeFromLocalStorage: lastSearchData.exchangeType
                });
            }
        } else {
            this.setState({
                guestsData: lastSearchData.guestsData,
                guestsDataFromLocalStorage: lastSearchData.guestsData,
                exchangeType: lastSearchData.exchangeType,
                exchangeTypeFromLocalStorage: lastSearchData.exchangeType
            });
        }
    }

    handleFormValidation() {
        // handle form validation
        if (this.form) {
            for (let i = 0; i < this.form.elements.length; i++) {
                $(this.form.elements[i])
                    .off('invalid change blur keyup')
                    .on('invalid change blur keyup', (event) => {
                        const $target = $(event.target);
                        if ($target.hasClass('reset') || this.state.isCalendarOpen) {
                            // Do not validate reset button
                            return;
                        }

                        // disable default invalid bubble message
                        if (event.type === 'invalid') {
                            event.preventDefault();
                        }

                        const index = this.invalidFields.indexOf($target.get(0));
                        if (event.target.validity && !event.target.validity.valid) {
                            // There is an error
                            if (event.target.validity.valueMissing) {
                                $target
                                    .closest('.form-group')
                                    .addClass('has-error')
                                    .attr('data-error', i18n.t('common:validity_valueMissing'));
                            } else if (event.target.validationMessage) {
                                $target
                                    .closest('.form-group')
                                    .addClass('has-error')
                                    .attr('data-error', event.target.validationMessage);
                            }
                            if (index < 0) {
                                this.invalidFields.push($target.get(0));
                            }
                        } else {
                            if ($target.length > 0 && $target.attr('data-warning')) {
                                // Show warnings
                                $target
                                    .closest('.form-group')
                                    .addClass('has-error')
                                    .attr('data-error', $target.data('warning'));
                            } else {
                                $target
                                    .closest('.form-group')
                                    .removeClass('has-error')
                                    .removeAttr('data-error');
                            }

                            if (index >= 0) {
                                this.invalidFields.splice(index, 1);
                            }
                        }
                        // revalidate message if model changed
                        if (event.target.name === 'model') {
                            // use timeout to trigger change after component has updated the field
                            setTimeout(() => {
                                const $textarea = $target.closest('form').find('[name="content"]');
                                if ($textarea.val().toString().length > 0) {
                                    $textarea.trigger('change');
                                }
                            }, 100);
                        }

                        // Enable/Disable submit button if at least one field is invalid
                        if (this.invalidFields.length > 0) {
                            this.setState({
                                disableSubmit: true
                            });
                        } else {
                            this.setState({
                                disableSubmit: false
                            });
                        }
                    });
            }
        }
    }

    datesChange(dates) {
        const isSameDaySelected = isSameDay(dates.start, dates.end);
        if (_.isEmpty(dates) || isSameDaySelected) {
            this.setState({
                startOn: null,
                endOn: null,
                numberOfNightsFromCalendar: 0,
                exchangeType: undefined,
                datesError: isSameDaySelected ? 'datesSameDay' : null
            });
            return;
        }

        const startOn = dates.start;
        const endOn = dates.end;
        const exchangeType = this.getExchangeTypeOnDateChange(startOn, endOn);
        const numberOfNightsFromCalendar = CalendarUtils.calculateNumberOfNightsOfSelectedDates([
            startOn,
            endOn
        ]);
        this.setState({
            startOn,
            endOn,
            exchangeType,
            numberOfNightsFromCalendar,
            datesError: null
        });
    }

    nbGuestChange(adults, children, babies) {
        this.setState({
            guestsData: {
                ...this.state.guestsData,
                size: {
                    beds: {
                        adults: adults ? adults : MainView.DEFAULT_ADULTS_NUMBER,
                        children: children ? children : 0,
                        babies: babies ? babies : 0
                    }
                }
            }
        });
    }

    exchangeTypeChange(value) {
        this.setState({
            exchangeType: value,
            hasTypeExchangeError: false
        });
    }

    isMessageTooShort(message) {
        return message.trim().split(/\s+/).length < 35;
    }

    checkFormValidity() {
        this.form.checkValidity();

        let isValid = true;
        const message = this.state.message;
        this.setState({ hasMessageError: _.isEmpty(message) });

        if (this.state.exchangeType === undefined) {
            this.setState({
                hasTypeExchangeError: true
            });
            isValid = false;
        }

        // don't submit form if empty
        if (_.isEmpty(message) || this.state.disableSubmit || !!this.state.datesError) {
            isValid = false;
        }

        // Don't submit if user wants to be contacted on available dates only and no dates selected
        if (
            this.props.home.get('contact_allowed') === HomeModel.CONTACT_NOT_ALLOWED_ON_UNAVAILABLE &&
            (!this.state.startOn || !this.state.endOn)
        ) {
            this.setState({ datesError: 'datesRequired' });
            isValid = false;
        }

        return isValid;
    }

    handleOnSubmit(event) {
        event.preventDefault();
        const message = this.state.message;

        if (!this.checkFormValidity()) {
            return;
        }

        const trackWarningTooShort = (action, text) => {
            const { fetchNavigation } = useGTM(this.props.user);
            Analytics.trackGTM(
                action === NAVIGATION_ACTION.CLICK ? 'button' : 'modal',
                fetchNavigation(action, NAVIGATION_AREAS.CONTACT_MODAL_WARNING_TOO_SHORT, text)
            );
        };

        if (this.isMessageTooShort(message)) {
            trackWarningTooShort(NAVIGATION_ACTION.VIEW, NAVIGATION_TEXT.REQUEST_MESSAGE_TOO_SHORT);

            this.setState({
                showAlertDialog: true,
                alertDialogProps: {
                    title: i18n.t('exchange:modale.exchangeRequest.alert.messageShort.title'),
                    children: i18n.t('exchange:modale.exchangeRequest.alert.messageShort.body', {
                        HostName: this.props.home.get('user').get('first_name')
                    }),
                    confirmActionText: i18n.t(
                        'exchange:modale.exchangeRequest.alert.messageShort.primary.cta'
                    ),
                    dismissActionText: i18n.t(
                        'exchange:modale.exchangeRequest.alert.messageShort.secondary.cta'
                    ),
                    iconStatus: 'info',
                    onConfirmAction: () => {
                        if (this.state.sending) {
                            return;
                        }
                        trackWarningTooShort(NAVIGATION_ACTION.CLICK, NAVIGATION_TEXT.KEEP_WRITING);
                        this.setState({
                            showAlertDialog: false,
                            alertDialogProps: {}
                        });
                    },
                    onDismissAction: () => {
                        if (this.state.sending) {
                            return;
                        }
                        trackWarningTooShort(NAVIGATION_ACTION.CLICK, NAVIGATION_TEXT.SEND_ANYWAY);
                        this.setState({
                            showAlertDialog: false,
                            alertDialogProps: {}
                        });
                        this.onSubmit();
                    }
                }
            });
        } else {
            this.onSubmit(event);
        }
    }

    async onSubmit() {
        const { onCloseModal } = this.props;
        const message = this.state.message;

        this.setState({ sending: true });

        // Update last search properties to reflect what the user entered
        const search = this.state.lastSearch
            ? Utils.deepclone(this.state.lastSearch)
            : {
                  query: {
                      availability: { date_range: {} }
                  }
              };

        search.query = Object.assign(
            {
                availability: { date_range: {} }
            },
            search.query
        );

        const getDate = (date) => {
            if (!date) {
                return null;
            }

            return typeof date === 'string' ? date : moment(date).format('YYYY-MM-DD');
        };

        const startOn = getDate(this.state.startOn);
        const endOn = getDate(this.state.endOn);

        // Update last search properties to reflect what the user entered
        if (startOn && endOn) {
            search.query.availability.date_range.from = startOn;
            search.query.availability.date_range.to = endOn;

            // Update exchange type
            if (this.state.exchangeType !== Availability.AVAILABLE.value) {
                search.query.availability.reciprocal =
                    parseInt(this.state.exchangeType, 10) === parseInt(Availability.RECIPROCAL.value, 10);
            }

            search.query.home = this.state.guestsData;
            this.props.updateLastSearch(search);
            Api.Search.setLastSearch(this.props.user.get('id'), search);
        }

        const guestNumber = this.state.guestsData
            ? _.reduce(this.state.guestsData.size.beds, (sum, elt) => sum + elt, 0)
            : 1;

        const body = {
            content: message,
            stayType:
                Number(this.state.exchangeType) === Availability.AVAILABLE.value
                    ? STAY_TYPE_WITH_GP
                    : EXCHANGE_TYPE_ID_TO_STAY_REQUEST_TYPE_NAME[this.state.exchangeType],
            startDate: startOn,
            endDate: endOn,
            homeId: this.props.home.get('id'),
            hostId: this.props.home.get('user').get('id'),
            guestId: this.props.user.id,
            guestNumber: guestNumber > 0 ? guestNumber : null
        };

        try {
            const response = await this.props.actions.createStayRequestV3(body);

            this.onMessageSuccess(response, { startOn, endOn, guests: this.state.guestsData?.size?.beds });
        } catch (error) {
            Sentry.captureException(error, {
                tags: { feature: 'create-stay-request' },
                extra: {
                    body
                }
            });
            this.onMessageFailure(error);
            onCloseModal();
        }
    }

    onMessageSuccess(response, sentRequest) {
        // show success view
        const { user } = this.props;
        this.props.onMessageSent(sentRequest);

        /** @Tracking ConversationCreated */
        this.props.track('contacted-member', {
            conversationId: response.conversationId
        });

        Analytics.trackGTM('MixpanelMessageSent', { userStatus: this.props.user.getStatus() });

        Analytics.trackGTM('conversation_created', {
            event_data: {
                exchange_type:
                    Number(this.state.exchangeType) === Availability.RECIPROCAL.value
                        ? 'reciprocal_exchange'
                        : 'guest_point_exchange',
                collection: user.isCollection()
            }
        });

        if (response.isUserFirstMessage) {
            this.props.track('First message');
        }
    }

    onMessageFailure(response) {
        this.setState({ sending: false });

        const errorsMapping = {
            'No links allowed': {
                title: 'exchange:alert.scam.link.email.title',
                body: 'exchange:alert.scam.link.email.body'
            },
            'No emails allowed': {
                title: 'exchange:alert.scam.link.email.title',
                body: 'exchange:alert.scam.link.email.body'
            },
            'Rate limit exceeded': {
                title: 'exchange:alert_dialog_scammers_rate_limit_title',
                body: 'exchange:alert_dialog_scammers_rate_limit_body'
            },
            'Rate limit exceeded too many request': {
                title: 'exchange:alert_dialog_scammers_rate_limit_100_title',
                body: 'exchange:alert_dialog_scammers_rate_limit_100_body'
            }
        };

        const error = errorsMapping[response?.errorData?.message?.errors];
        if (error) {
            this.setState({
                showAlertDialog: true,
                alertDialogProps: {
                    title: i18n.t(error.title),
                    children: i18n.t(error.body),
                    confirmActionText: i18n.t('ok'),
                    iconStatus: 'error',
                    onConfirmAction: () => {
                        this.setState({
                            showAlertDialog: false,
                            alertDialogProps: {}
                        });
                    }
                }
            });
            return;
        }

        let text = i18n.t('exchange:contact.error_message');
        if (response.code === 409) {
            text = i18n.t('exchange:notperiod');
        }
        // dispay alert error message
        sweetAlert({
            icon: 'error',
            title: i18n.t('exchange:contact.title', {
                first_name: this.props.home.get('user').get('first_name')
            }),
            text
        });
    }

    getExchangeTypeOnDateChange(startOn, endOn, fallbackValue = undefined) {
        if (!startOn || !endOn) {
            return undefined;
        }
        let exchangeType = fallbackValue;
        const exchangeDateRange = moment.range(startOn, endOn);
        const matchingAvailabilities = new Set(
            this.props.availabilities
                .filter((av) => av.range().overlaps(exchangeDateRange))
                .map((av) => av.get('type'))
        );
        const firstMatchingAvailability = matchingAvailabilities.values().next().value;
        if (matchingAvailabilities.size === 1 && firstMatchingAvailability !== Availability.AVAILABLE.type) {
            exchangeType = Availability[firstMatchingAvailability].value;
        } else {
            exchangeType = fallbackValue;
        }
        return exchangeType;
    }

    isExchangeNotCoveredBySubscription() {
        const { user } = this.props;
        const { endOn } = this.state;

        return Boolean(
            endOn &&
                user.isSubscriber() &&
                !user.get('subscription').auto_renew &&
                user.has('subscription_end_date') &&
                user.get('subscription_end_date') &&
                user.get('subscription_end_date').isBefore(Utils.getDayAtMidnightLocalTime(endOn))
        );
    }

    renderAlertDialog() {
        if (!this.state.showAlertDialog) {
            return null;
        }

        return <AlertDialog isOpen {...this.state.alertDialogProps} />;
    }

    onChangeMessage(value) {
        this.setState({ message: value, hasMessageError: _.isEmpty(value) });
    }

    resetState() {
        this.setState({
            message: '',
            startOn: this.state.startOnFromLocalStorage,
            endOn: this.state.endOnFromLocalStorage,
            exchangeType: this.state.exchangeTypeFromLocalStorage,
            isCalendarOpen: false,
            disableSubmit: false,
            sending: false,
            openGuestNbPopup: false,
            guestsData: this.state.guestsDataFromLocalStorage,
            showAlertDialog: false,
            alertDialogProps: {},
            hasMessageError: false,
            numberOfNightsFromCalendar: null,
            hasTypeExchangeError: false,
            datesError: null
        });
    }

    render() {
        const { showModal, onCloseModal, availabilities, home, user, conversationsStats } = this.props;
        const {
            disableSubmit,
            previousConversation,
            sending,
            startOn,
            endOn,
            exchangeType,
            guestsData,
            hasTypeExchangeError,
            datesError
        } = this.state;

        let { numberOfNightsFromCalendar } = this.state;
        const hostMinimumNumberOfNights = home.getMinNights();

        if (!numberOfNightsFromCalendar) {
            numberOfNightsFromCalendar = CalendarUtils.calculateNumberOfNightsOfSelectedDates([
                this.state.startOn,
                this.state.endOn
            ]);
        }

        const displayMinimumOfNightBanner =
            numberOfNightsFromCalendar !== 0 && numberOfNightsFromCalendar < hostMinimumNumberOfNights;
        const displayNotCoveredBySubscriptionBanner = this.isExchangeNotCoveredBySubscription();

        return (
            <>
                {this.renderAlertDialog()}
                <Modal
                    title={i18n.t('exchange:contact.title', {
                        first_name: home.get('user').get('first_name')
                    })}
                    onInteractOutside={(event) => event.preventDefault()}
                    isOpen={showModal}
                    isClosable
                    onClose={() => {
                        this.resetState();
                        onCloseModal();
                    }}
                    size="large"
                    confirmActionText={i18n.t('exchange:contact.send_request')}
                    confirmActionDisabled={disableSubmit || sending}
                    confirmActionLoading={sending}
                    onConfirmAction={(event) => {
                        event.preventDefault();

                        this.form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                    }}
                    alternativeAction={
                        <Link
                            color="secondary"
                            onClick={(event) => {
                                event.preventDefault();
                                this.resetState();
                                onCloseModal();
                            }}
                        >
                            {i18n.t('exchange:page.exchangeRequest.cancelRequest.cta')}
                        </Link>
                    }
                    onAlternativeAction={onCloseModal}
                >
                    <section className="modal-contact">
                        <main>
                            {(() => {
                                if (previousConversation) {
                                    return (
                                        <Callout status="warning" hasIcon={true}>
                                            <Text>
                                                {i18n.t(
                                                    'exchange:page.exchangeRequest.alreadyContacted.callout.body',
                                                    {
                                                        Hostname: home.get('user').get('first_name'),
                                                        dateContacted: moment(
                                                            previousConversation.last_message.send_at
                                                        ).format('L')
                                                    }
                                                )}
                                            </Text>
                                            <Button
                                                hierarchy="secondary"
                                                onClick={() =>
                                                    (window.location = i18n.t('exchange:conversation.url', {
                                                        id: previousConversation.id
                                                    }))
                                                }
                                            >
                                                {i18n.t(
                                                    'exchange:page.exchangeRequest.alreadyContacted.callout.cta'
                                                )}
                                            </Button>
                                        </Callout>
                                    );
                                }
                            })()}
                            <form
                                action="#"
                                onSubmit={this.handleOnSubmit}
                                ref={(form) => {
                                    this.form = form;
                                }}
                                noValidate
                            >
                                <div className="popup-body">
                                    <div className="popup-field-block">
                                        <div className="form-groups-inline">
                                            <div className="form-group form-group-date-range">
                                                <DateRangePickerSelect
                                                    availabilities={availabilities}
                                                    datesChange={this.datesChange}
                                                    datesError={datesError}
                                                    startOn={startOn}
                                                    endOn={endOn}
                                                    contactOnUnavailablePeriods={
                                                        home.get('contact_allowed') ===
                                                        HomeModel.CONTACT_ALLOWED_ON_UNAVAILABLE
                                                    }
                                                />
                                            </div>
                                        </div>

                                        {displayMinimumOfNightBanner ||
                                        displayNotCoveredBySubscriptionBanner ? (
                                            <Flex
                                                direction="column"
                                                gap="3"
                                                justifyContent="stretch"
                                                alignItems="stretch"
                                            >
                                                {displayMinimumOfNightBanner && (
                                                    <Callout status="info" hasIcon>
                                                        <Text
                                                            dangerouslySetInnerHTML={{
                                                                __html: i18n.t(
                                                                    'home:home_view_calendar_minimum_number_of_nights',
                                                                    {
                                                                        name: this.props.home
                                                                            .get('user')
                                                                            .get('first_name'),
                                                                        number: hostMinimumNumberOfNights
                                                                    }
                                                                )
                                                            }}
                                                        />
                                                    </Callout>
                                                )}
                                                {displayNotCoveredBySubscriptionBanner && (
                                                    <Callout status="warning" hasIcon>
                                                        {i18n.t(
                                                            'exchange:contact.warning.subscription.expired'
                                                        )}
                                                    </Callout>
                                                )}
                                            </Flex>
                                        ) : null}
                                    </div>

                                    <div className="popup-field-block">
                                        <div>
                                            <p>{i18n.t('common:guests')}</p>
                                        </div>
                                        <div className="form-group form-group-guests-nb">
                                            <CounterGuestNb
                                                guestsData={guestsData}
                                                onNbGuestChange={this.nbGuestChange}
                                            />
                                        </div>
                                    </div>
                                    <div className="popup-field-block type-exchange">
                                        <ExchangeTypeSelect
                                            startOn={startOn}
                                            endOn={endOn}
                                            availabilities={availabilities}
                                            exchangeType={exchangeType}
                                            onChange={this.exchangeTypeChange}
                                            hasTypeExchangeError={hasTypeExchangeError}
                                        />
                                    </div>
                                    <MessageField
                                        message={this.state.message}
                                        home={home}
                                        user={user}
                                        exchangeType={exchangeType}
                                        hasError={this.state.hasMessageError}
                                        onChange={this.onChangeMessage}
                                        conversationsStats={conversationsStats}
                                    />
                                </div>
                            </form>
                        </main>
                    </section>
                </Modal>
            </>
        );
    }
}

const mapStateToProps = (state) => ({
    accessToken: state.auth.accessToken,
    conversationsStats: state.conversations.stats
});

function mapDispatchToProps(dispatch) {
    return {
        fetchConversationsStats: bindActionCreators(fetchConversationsStats, dispatch),
        actions: bindActionCreators(
            {
                createStayRequestV3
            },
            dispatch
        )
    };
}

export default compose(connect(mapStateToProps, mapDispatchToProps), withAnalytics)(MainView);
